import { LettureOdoo } from "../utils/lettureOdoo";
import { apiClient } from "./client";
import { InstallationSchedule, TipoDispositivo } from "../types";

export interface ScheduleFilters {
  stato?: string;
  zona?: string;
  installatore_id?: string;
  tipo_dispositivo?: TipoDispositivo;
  data_da?: string;
  data_a?: string;
  ritardo?: boolean;
  /** Solo le installazioni che aspettano l'Attivazione collegata. */
  attivazione_mancante?: boolean;
  /**
   * Ricerca testuale su insegna, localita', indirizzo, matricola e tecnico.
   *
   * La usano due cose diverse: la barra dell'elenco e l'autocomplete "Collegata
   * a installazione" delle Attivazioni.
   */
  search?: string;
}

export interface RitardiStats {
  ritardi: {
    totale: number;
    per_modulo: { installazioni: number; sopralluoghi: number; attivazioni: number; interventi: number };
  };
}

export async function getRitardi(): Promise<RitardiStats> {
  const { data } = await apiClient.get<RitardiStats>("/dashboard/ritardi");
  return data;
}

export async function listSchedule(filters: ScheduleFilters = {}): Promise<{ records: InstallationSchedule[] }> {
  const { data } = await apiClient.get<{ records: InstallationSchedule[] }>("/schedule", { params: filters });
  return data;
}

/** Suggerimento cliente per una scheda scollegata (Punto B, backfill assistito). */
export interface ClienteSuggestion {
  cliente_id: string;
  cliente_nome: string;
  sede_id: string;
  sede_nome: string;
}

export async function suggerisciCliente(id: string): Promise<{ suggestion: ClienteSuggestion | null }> {
  const { data } = await apiClient.get<{ suggestion: ClienteSuggestion | null }>(`/schedule/${id}/suggerisci-cliente`);
  return data;
}

/** Singola scheda (scoped) — dettaglio read-only del cliente nel Calendario unificato. */
export async function getSchedule(id: string): Promise<{ record: InstallationSchedule }> {
  const { data } = await apiClient.get<{ record: InstallationSchedule }>(`/schedule/${id}`);
  return data;
}

/**
 * Esito del censimento del dispositivo chiesto con la spunta del form.
 * Assente (`null`) quando la spunta non c'era o non c'era niente da censire.
 *
 * `occupata` è il caso che il vincolo del database rende possibile: quel pezzo
 * ha già la SUA installazione (`installation_schedule.device_id` è UNIQUE), e
 * questa resta senza aggancio. Non è un errore — il device è censito lo stesso,
 * che era lo scopo — ma va detto a chi salva, o si troverebbe un record che
 * sembra collegato e non lo è.
 */
export interface EsitoCensimentoDevice {
  device_id: string;
  matricola: string;
  /** false = la matricola era già censita: il device non è stato toccato. */
  creato: boolean;
  /** true = è stata aperta la relazione con il cliente/sede del record. */
  relazione_creata: boolean;
  occupata: boolean;
}

/**
 * Il payload porta anche `censisci_device` e `censimento_tipo_possesso`, che
 * NON sono colonne di `installation_schedule`: la rotta le legge dal body
 * grezzo. Da qui il tipo allargato, invece di un `Partial` secco che le
 * rifiuterebbe.
 */
export type SchedulePayload = Omit<Partial<InstallationSchedule>, "sopralluoghi"> & {
  censisci_device?: boolean;
  censimento_tipo_possesso?: string | null;
  /**
   * Gli ID dei sopralluoghi da collegare — non gli oggetti che il GET
   * restituisce. Le due forme sono diverse di proposito: in lettura serve
   * qualcosa da mostrare (insegna, data, stato), in scrittura basta la chiave.
   *
   * Omesso = non toccare i collegamenti. Array vuoto = scollegali tutti.
   */
  sopralluoghi?: string[];
};

export async function createSchedule(
  payload: SchedulePayload
): Promise<{ record: InstallationSchedule; censimento: EsitoCensimentoDevice | null }> {
  const { data } = await apiClient.post<{
    record: InstallationSchedule;
    censimento: EsitoCensimentoDevice | null;
  }>("/schedule", payload);
  return data;
}

export async function updateSchedule(
  id: string,
  payload: SchedulePayload
): Promise<{ record: InstallationSchedule; censimento: EsitoCensimentoDevice | null }> {
  const { data } = await apiClient.patch<{
    record: InstallationSchedule;
    censimento: EsitoCensimentoDevice | null;
  }>(`/schedule/${id}`, payload);
  return data;
}

export async function deleteSchedule(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/schedule/${id}`);
  return data;
}

export interface ScheduleBulkDeleteFilters {
  stato?: string;
  zona?: string;
  installatore_id?: string;
  tipo_dispositivo?: TipoDispositivo;
  data_da?: string;
  data_a?: string;
}

export async function bulkDeletePreview(filters: ScheduleBulkDeleteFilters): Promise<{ count: number }> {
  const { data } = await apiClient.get<{ count: number }>("/schedule/bulk-delete/preview", { params: filters });
  return data;
}

export async function bulkDeleteSchedule(filters: ScheduleBulkDeleteFilters): Promise<{ deleted: number }> {
  const { data } = await apiClient.delete<{ deleted: number }>("/schedule/bulk", { params: filters });
  return data;
}

export async function importSchedule(records: Partial<InstallationSchedule>[]): Promise<{ imported: number }> {
  const { data } = await apiClient.post<{ imported: number }>("/schedule/import", { records });
  return data;
}

export interface ScheduleStats {
  by_stato: Record<string, number>;
  prossimi_7_giorni: InstallationSchedule[];
}

export async function getScheduleStats(): Promise<ScheduleStats> {
  const { data } = await apiClient.get<ScheduleStats>("/schedule/stats");
  return data;
}

export async function geocodeSchedule(id: string): Promise<{ record: InstallationSchedule }> {
  const { data } = await apiClient.post<{ record: InstallationSchedule }>(`/schedule/${id}/geocode`);
  return data;
}

/** Legge gli eventi da Odoo Calendar e aggiorna/crea le righe locali collegate. */
export async function pullScheduleFromOdoo(): Promise<{ synced: number; lettura?: LettureOdoo }> {
  const { data } = await apiClient.post<{ synced: number; lettura?: LettureOdoo }>("/schedule/sync/pull");
  return data;
}

/** Crea o aggiorna l'evento Odoo Calendar corrispondente a questa riga. */
export async function pushScheduleToOdoo(id: string): Promise<{ record: InstallationSchedule }> {
  const { data } = await apiClient.post<{ record: InstallationSchedule }>(`/schedule/${id}/sync/push`);
  return data;
}

export interface ScheduleStatsFull {
  totale: number;
  mese_corrente: number;
  percentuale_completamento: number;
  by_stato: Record<string, number>;
  by_zona: Record<string, number>;
  by_installatore: Record<string, number>;
  by_tipo_dispositivo: Record<string, number>;
}

/** Solo admin — usato dalla pagina Statistiche. */
export async function getScheduleStatsFull(): Promise<ScheduleStatsFull> {
  const { data } = await apiClient.get<ScheduleStatsFull>("/schedule/stats/full");
  return data;
}
